import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Visibility as ViewIcon,
} from "@mui/icons-material";
import { apiRequest } from "../config/api";
import { useAuth } from "../contexts/AuthContext";

interface DeletionRequest {
  id: string;
  playerId: string;
  playerName: string;
  username: string;
  email: string;
  requestedAt: Date | string;
  status: "pending" | "approved" | "rejected";
  processedAt?: Date | string;
  processedBy?: string;
}

function AccountDeletionRequests() {
  const { token } = useAuth();
  const [requests, setRequests] = useState<DeletionRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [selectedRequest, setSelectedRequest] =
    useState<DeletionRequest | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmationType, setConfirmationType] = useState<
    "approve" | "reject"
  >("approve");
  const [rejectionReason, setRejectionReason] = useState("");

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const response = await apiRequest("/api/admin/deletion-requests", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setRequests(data);
      } else {
        setError("Failed to fetch deletion requests");
      }
    } catch (error) {
      console.error("Error fetching deletion requests:", error);
      setError("Failed to fetch deletion requests");
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (requestId: string) => {
    setProcessing(requestId);
    setError("");
    setSuccess("");

    try {
      const response = await apiRequest(
        `/api/admin/deletion-requests/${requestId}/approve`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.ok) {
        setSuccess("Account deletion approved and processed successfully");
        fetchRequests();
      } else {
        const data = await response.json();
        setError(data.error || "Failed to approve deletion");
      }
    } catch (error) {
      console.error("Error approving deletion:", error);
      setError("Failed to approve deletion");
    } finally {
      setProcessing(null);
      setConfirmDialogOpen(false);
    }
  };

  const handleReject = async (requestId: string) => {
    setProcessing(requestId);
    setError("");
    setSuccess("");

    try {
      const response = await apiRequest(
        `/api/admin/deletion-requests/${requestId}/reject`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ reason: rejectionReason }),
        },
      );

      if (response.ok) {
        setSuccess("Deletion request rejected");
        fetchRequests();
        setRejectionReason("");
      } else {
        const data = await response.json();
        setError(data.error || "Failed to reject deletion");
      }
    } catch (error) {
      console.error("Error rejecting deletion:", error);
      setError("Failed to reject deletion");
    } finally {
      setProcessing(null);
      setConfirmDialogOpen(false);
    }
  };

  const openConfirmDialog = (
    request: DeletionRequest,
    type: "approve" | "reject",
  ) => {
    setSelectedRequest(request);
    setConfirmationType(type);
    setConfirmDialogOpen(true);
  };

  const getStatusColor = (
    status: string,
  ): "default" | "warning" | "success" | "error" => {
    switch (status) {
      case "pending":
        return "warning";
      case "approved":
        return "success";
      case "rejected":
        return "error";
      default:
        return "default";
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom fontWeight="bold">
          Account Deletion Requests (GDPR)
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage player account deletion requests. Approving will permanently
          delete all player data.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess("")}>
          {success}
        </Alert>
      )}

      <Card>
        <CardContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <strong>Player Name</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Username</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Email</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Requested At</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Status</strong>
                  </TableCell>
                  <TableCell align="right">
                    <strong>Actions</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {requests.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ py: 4 }}
                      >
                        No deletion requests found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  requests.map((request) => (
                    <TableRow key={request.id}>
                      <TableCell>{request.playerName}</TableCell>
                      <TableCell>{request.username}</TableCell>
                      <TableCell>{request.email}</TableCell>
                      <TableCell>
                        {new Date(request.requestedAt).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={request.status.toUpperCase()}
                          color={getStatusColor(request.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Box
                          sx={{
                            display: "flex",
                            gap: 1,
                            justifyContent: "flex-end",
                          }}
                        >
                          {request.status === "pending" && (
                            <>
                              <Button
                                variant="contained"
                                color="error"
                                size="small"
                                startIcon={<ApproveIcon />}
                                onClick={() =>
                                  openConfirmDialog(request, "approve")
                                }
                                disabled={processing === request.id}
                              >
                                {processing === request.id ? (
                                  <CircularProgress size={20} />
                                ) : (
                                  "Approve & Delete"
                                )}
                              </Button>
                              <Button
                                variant="outlined"
                                color="primary"
                                size="small"
                                startIcon={<RejectIcon />}
                                onClick={() =>
                                  openConfirmDialog(request, "reject")
                                }
                                disabled={processing === request.id}
                              >
                                Reject
                              </Button>
                            </>
                          )}
                          {request.status !== "pending" && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {request.status === "approved"
                                ? "Deleted"
                                : "Request Rejected"}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => !processing && setConfirmDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {confirmationType === "approve"
            ? "⚠️ Confirm Account Deletion"
            : "Reject Deletion Request"}
        </DialogTitle>
        <DialogContent>
          {confirmationType === "approve" ? (
            <>
              <Alert severity="error" sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight="bold">
                  This action is PERMANENT and cannot be undone!
                </Typography>
              </Alert>
              <Typography variant="body1" paragraph>
                You are about to permanently delete the account for:
              </Typography>
              <Box
                sx={{
                  bgcolor: "background.default",
                  p: 2,
                  borderRadius: 1,
                  mb: 2,
                }}
              >
                <Typography variant="body2">
                  <strong>Player:</strong> {selectedRequest?.playerName}
                </Typography>
                <Typography variant="body2">
                  <strong>Username:</strong> {selectedRequest?.username}
                </Typography>
                <Typography variant="body2">
                  <strong>Email:</strong> {selectedRequest?.email}
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                This will delete:
              </Typography>
              <Box component="ul" sx={{ pl: 3 }}>
                <li>
                  <Typography variant="body2">
                    Player profile and personal data
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2">
                    Match history (anonymized)
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2">
                    All league registrations
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2">Account credentials</Typography>
                </li>
              </Box>
            </>
          ) : (
            <>
              <Typography variant="body1" paragraph>
                Reject deletion request for:{" "}
                <strong>{selectedRequest?.playerName}</strong>
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Reason for Rejection (optional)"
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Explain why the deletion request is being rejected..."
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmDialogOpen(false)}
            disabled={!!processing}
          >
            Cancel
          </Button>
          <Button
            onClick={() =>
              confirmationType === "approve"
                ? handleApprove(selectedRequest!.id)
                : handleReject(selectedRequest!.id)
            }
            variant="contained"
            color={confirmationType === "approve" ? "error" : "primary"}
            disabled={!!processing}
            startIcon={processing && <CircularProgress size={20} />}
          >
            {processing
              ? "Processing..."
              : confirmationType === "approve"
                ? "Confirm Deletion"
                : "Reject Request"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default AccountDeletionRequests;
